import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import ReactMarkdown from "react-markdown";
import { ArrowClockwise, Handshake, Info, Lock } from "@phosphor-icons/react";

/**
 * Job-coach view of a coachee's Conflicthanteringstest session.
 * Read-only transcript + a "Reset" button so the coachee can retake the test.
 */
export default function ConflictTestCoachPanel({ coacheeId, coacheeName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const fetchSession = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/conflict-test`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSession(); }, [coacheeId]);

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      await api.post(`/coachees/${coacheeId}/conflict-test/reset`);
      setConfirmOpen(false);
      await fetchSession();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  const available = session?.available !== false;
  const used = session?.user_message_count ?? 0;
  const cap = session?.max_user_messages ?? 50;
  const limitReached = session?.limit_reached === true || used >= cap;
  const messages = session?.messages || [];

  return (
    <div className="space-y-4 max-w-3xl" data-testid="conflict-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <Handshake size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">
              Conflicthanteringstest (Cybernetic Big Five)
            </h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {coacheeName || "De coachee"} kan deze test slechts één keer afronden
              (max {cap} berichten). Klik op <em>Resetten</em> om de coachee opnieuw
              te laten beginnen.
            </p>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-zinc-500" data-testid="conflict-coach-counter">
                Berichten gebruikt: <strong className="text-zinc-900">{used}</strong> / {cap}
                {limitReached && (
                  <span className="ml-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/30 rounded-sm">
                    Limiet bereikt
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={resetting || !available}
                data-testid="conflict-reset-btn"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
              >
                <ArrowClockwise size={16} /> Test resetten
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm"
          data-testid="conflict-coach-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : !available ? (
        <div className="neuro-card p-6 bg-white" data-testid="conflict-coach-locked">
          <div className="flex items-start gap-3">
            <Lock size={22} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">
                Nog niet beschikbaar voor de coachee
              </h4>
              <p className="text-sm text-zinc-600 mt-1">
                Stel eerst het breintype definitief in via het tabblad <em>Breintype</em>.
                Pas daarna kan {coacheeName || "de coachee"} deze test starten.
              </p>
            </div>
          </div>
        </div>
      ) : messages.length === 0 ? (
        <div className="neuro-card p-6 bg-white" data-testid="conflict-coach-empty">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Nog niet gestart</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {coacheeName || "De coachee"} heeft de conflicthanteringstest nog niet gestart.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="neuro-card p-4 bg-white max-h-[480px] overflow-y-auto space-y-3" data-testid="conflict-coach-thread">
          {messages.map((m, i) => (
            <Bubble key={m.ts ? `${m.ts}-${m.role}` : `i-${i}`} role={m.role} content={m.content} />
          ))}
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !resetting && setConfirmOpen(false)}>
          <div
            className="bg-white p-6 max-w-md w-full rounded-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="conflict-reset-confirm"
          >
            <h4 className="font-display text-lg font-bold tracking-tight">Test resetten?</h4>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              De volledige chatgeschiedenis van de conflicthanteringstest wordt gewist.
              {" "}{coacheeName || "De coachee"} kan daarna opnieuw beginnen. Deze actie
              kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-2 mt-4 justify-end">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={resetting}
                data-testid="conflict-reset-cancel"
                className="px-4 py-2 text-sm font-medium border border-zinc-300 rounded-sm hover:bg-zinc-100 transition-colors"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={resetting}
                data-testid="conflict-reset-confirm-btn"
                className="px-4 py-2 text-sm font-semibold uppercase tracking-wide bg-[#E53935] text-white rounded-sm hover:bg-[#C62828] transition-colors disabled:bg-zinc-400"
              >
                {resetting ? "Resetten..." : "Ja, resetten"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={"flex " + (isUser ? "justify-end" : "justify-start")}>
      <div
        className={
          "max-w-[80%] px-3.5 py-2.5 rounded-sm text-sm whitespace-pre-wrap leading-relaxed " +
          (isUser
            ? "bg-zinc-900 text-white"
            : "bg-zinc-100 text-zinc-900 border border-zinc-200")
        }
      >
        {isUser ? content : (
          <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight prose-p:my-2 prose-li:my-0">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
